import { FormControl, NG_ASYNC_VALIDATORS, ValidationErrors } from '@angular/forms';
import { Directive, EventEmitter, HostBinding, Input, OnDestroy, Output } from '@angular/core';
import { catchError, finalize, map, Observable, of, switchMap, timer, Subscription } from 'rxjs';
import { OrderCouponService } from '@reg/shared/service';
import { OrderCoupon, PagedResponse } from '@reg/shared/model';

@Directive({
    selector: '[floAsyncCouponCodeValidator]',
    providers: [
        { provide: NG_ASYNC_VALIDATORS, useExisting: CouponCodeCheckDirective, multi: true },
    ],
})
export class CouponCodeCheckDirective implements OnDestroy {
    @Output() public isValidatingChange: EventEmitter<boolean> = new EventEmitter<boolean>();
    @Output() public existsChange: EventEmitter<boolean> = new EventEmitter<boolean>();
    @Input() public orderCouponId: string;

    private _isValidating: boolean = false;
    @HostBinding('class.is-validating')
    set isValidating(isValidating: boolean) {
        this._isValidating = isValidating;
        this.isValidatingChange.emit(isValidating);
    }

    get isValidating(): boolean {
        return this._isValidating;
    }

    private formControl: FormControl;
    private defaultDelay: number = 600; // wait 600 milliseconds before validating so we do not validate on every key stroke
    private delay: number = this.defaultDelay;
    private valueChangedSubscription: Subscription;

    constructor(private orderCouponService: OrderCouponService) {
    }

    public validate(control: FormControl): Observable<ValidationErrors> {
        this.isValidating = true;

        if (!this.formControl) {
            this.formControl = control;

            // First time loading so lets validate immediately
            this.delay = 0;
        }

        if (!control.value) {
            this.existsChange.emit(false);
            this.isValidating = false;
            return of(null);
        }

        // This timer will be auto canceled if a new value comes in before making the service call
        // because the control will unsubscribe from the previous validation check
        return timer(this.delay).pipe(switchMap(() => {
            this.delay = this.defaultDelay;

            return this.orderCouponService.getIndexListResponse(null, [`code=${control.value}`], false)
                .pipe(
                    map((resp: PagedResponse) => {
                        if (!control.value) {
                            this.existsChange.emit(null);
                            return null;
                        }

                        const exists: boolean = resp.totalCount > 0 && !resp.recordList.find(
                            (o: OrderCoupon) => o.id === this.orderCouponId,
                        );

                        this.existsChange.emit(exists);

                        return exists ? { exists: true } : null;
                    }),
                    finalize(() => {
                        this.isValidating = false;
                    }),
                    catchError(() => {
                        this.existsChange.emit(false);
                        return of({ invalid: true });
                    }),
                );
        }));
    }

    public ngOnDestroy(): void {
        if (this.valueChangedSubscription) {
            this.valueChangedSubscription.unsubscribe();
        }
    }
}
